/**
 * Jira-tab TanStack Query hook (Plan Jira-tab Task A3).
 *
 * Unwraps the server envelope and returns the inner `JiraCatalogView`. The
 * catalog needs no filters (static request-type inventory), so the query key
 * is constant and there is no `placeholderData` / filter re-keying like the
 * Engagement hooks. `staleTime: 60_000` matches the rest of the dashboard's
 * cadence.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import {
  getJiraCatalog,
  getJiraGaps,
  getJiraUsage,
  getJiraTypeUsages,
} from '@/services/jira-api';
import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import type {
  JiraCatalogView,
  JiraGaps,
  JiraUsageCount,
  JiraUsageRow,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useJiraCatalog() {
  return useQuery<JiraCatalogView>({
    queryKey: ['jira', 'catalog'],
    queryFn: async () => (await getJiraCatalog()).data,
    staleTime: STALE_TIME,
  });
}

/**
 * Coverage gaps — filter-aware (mirrors the Engagement hooks): keyed by the
 * semantic filter set via `filtersQuestion(filters)`, with `keepPreviousData`
 * — which since the recount rule no longer keeps the previous window's rows
 * on screen (hooks/use-filtered-query.ts); the tables blank instead.
 */
export function useJiraGaps(filters: Filters) {
  return useFilteredQuery<JiraGaps>({
    queryKey: ['jira', 'gaps', filtersQuestion(filters)],
    queryFn: async () => (await getJiraGaps(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Per-request-type usage counters — filter-aware (mirrors `useJiraGaps`).
 * Keyed by the semantic filter set via `filtersQuestion(filters)` with
 * `keepPreviousData`. Those drafts/clicks columns blank now rather
 * than standing as the previous window's counts — before this they printed
 * `0 / 0`, because the table was gated on the (global) catalog query.
 */
export function useJiraUsage(filters: Filters) {
  return useFilteredQuery<JiraUsageCount[]>({
    queryKey: ['jira', 'usage', filtersQuestion(filters)],
    queryFn: async () => (await getJiraUsage(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Per-type usage drill-down — the trace rows for one request type. Lazy: the
 * caller (an expanded catalog row) passes `enabled=true` only when the row is
 * open, so the endpoint is hit once per expansion instead of for every row.
 * Keyed by `requestTypeId` alongside the filter set so each row's drill-down
 * caches independently and re-fetches when the window/role changes.
 */
export function useJiraTypeUsages(
  requestTypeId: number,
  filters: Filters,
  enabled: boolean,
) {
  return useFilteredQuery<JiraUsageRow[]>({
    queryKey: ['jira', 'usage', requestTypeId, filtersQuestion(filters)],
    queryFn: async () => (await getJiraTypeUsages(requestTypeId, filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    enabled,
  });
}
